// Client-side API client and React hooks for the Repair Knowledge Base.
// Reads: public (published only). Writes: require admin token.

import { useState, useEffect, useCallback } from 'react';

const API_BASE = import.meta.env.DEV ? 'http://localhost:3001' : '';
const TOKEN_KEY = 'gfs_admin_token';

function authHeaders(): Record<string, string> {
  const token = sessionStorage.getItem(TOKEN_KEY);
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function json(headers: Record<string, string> = {}): Record<string, string> {
  return { 'Content-Type': 'application/json', ...headers };
}

// ── Types ─────────────────────────────────────────────────────────────────────

export type RepairStatus   = 'draft' | 'published' | 'internal';
export type RepairResult   = '' | 'repaired' | 'partially-repaired' | 'no-fix' | 'board-donor' | 'further-diagnosis';
export type MeasurementType = 'voltage' | 'resistance' | 'diode' | 'current' | 'injection' | 'continuity' | 'other';
export type MeasurementUnit = 'V' | 'mV' | 'Ω' | 'kΩ' | 'MΩ' | 'A' | 'mA' | 'diode' | 'other';
export type ErrorSource     = 'uart' | 'console-service-tool' | 'system-error-code' | 'other';
export type ComponentType   = 'capacitor' | 'fuse' | 'mosfet' | 'ic' | 'hdmi-encoder' | 'southbridge' | 'ram' | 'ssd-controller' | 'power-ic' | 'connector' | 'other';
export type FailureMode     = 'shorted' | 'open' | 'corroded' | 'missing' | 'cracked' | 'burned' | 'incorrect-reading' | 'other';

export interface RepairError {
  id?:         string;
  source:      ErrorSource;
  error_code:  string;
  notes:       string;
  sort_order:  number;
}

export interface RepairMeasurement {
  id?:                    string;
  reference_line:         string;
  measurement_type:       MeasurementType;
  expected_value:         string;
  measured_value:         string;
  units:                  MeasurementUnit;
  is_verified_reference:  boolean;
  notes:                  string;
  sort_order:             number;
}

export interface RepairVoltageInjection {
  id?:              string;
  injection_point:  string;
  injected_voltage: string;
  current_limit:    string;
  result:           string;
  component_heated: string;
  notes:            string;
  sort_order:       number;
}

export interface RepairComponent {
  id?:            string;
  component_ref:  string;
  component_type: ComponentType;
  failure_mode:   FailureMode;
  notes:          string;
  sort_order:     number;
}

export interface RepairMedia {
  id?:          string;
  storage_path: string;
  public_url:   string;
  mime_type:    string;
  caption:      string;
  is_featured:  boolean;
  sort_order:   number;
  created_at?:  string;
}

export interface RepairCase {
  id:                  string;
  slug:                string;
  status:              RepairStatus;
  title:               string;
  seo_title:           string;
  seo_description:     string;
  device_family:       string;
  device_model:        string;
  board_revision:      string;
  device_family_id:    string | null;
  board_revision_id:   string | null;
  repair_categories:   string[];
  symptoms_summary:    string[];
  detailed_symptoms:   string;
  root_cause:          string;
  repair_summary:      string;
  components_replaced: string;
  jumper_trace_repair: string;
  post_repair_testing: string;
  result:              RepairResult;
  public_notes:        string;
  internal_notes?:     string;  // only present for admin fetches
  featured_image_url:  string;
  tags:                string[];
  published_at:        string | null;
  created_at:          string;
  updated_at:          string;
  // embedded children
  repair_case_errors?:             RepairError[];
  repair_case_measurements?:       RepairMeasurement[];
  repair_case_voltage_injections?: RepairVoltageInjection[];
  repair_case_components?:         RepairComponent[];
  repair_case_media?:              RepairMedia[];
}

export type RepairCaseDraft = Omit<RepairCase, 'id' | 'slug' | 'created_at' | 'updated_at' | 'published_at'> & {
  slug?: string;
  errors?:             RepairError[];
  measurements?:       RepairMeasurement[];
  voltage_injections?: RepairVoltageInjection[];
  components?:         RepairComponent[];
  media_updates?:      { id: string; sort_order?: number; caption?: string; is_featured?: boolean }[];
};

// ── API functions ─────────────────────────────────────────────────────────────

export async function fetchRepairCases(
  adminMode = false,
  filters: { device_family_id?: string; board_revision_id?: string | 'null' } = {},
): Promise<RepairCase[]> {
  const headers = adminMode ? authHeaders() : {};
  const params = new URLSearchParams();
  if (filters.device_family_id) params.set('device_family_id', filters.device_family_id);
  if (filters.board_revision_id) params.set('board_revision_id', filters.board_revision_id);
  const qs = params.toString();
  const res = await fetch(`${API_BASE}/api/cms/repair-cases${qs ? `?${qs}` : ''}`, { headers });
  if (res.status === 401) throw new Error('Unauthorized');
  if (!res.ok) throw new Error(`Failed to fetch cases: ${res.status}`);
  const body = await res.json() as { cases: RepairCase[]; fallback_used?: boolean };
  if (body.fallback_used) console.warn('[repair-kb] CMS FALLBACK USED — cases list returned empty due to Supabase error');
  return body.cases;
}

export async function fetchRepairCase(idOrSlug: string, adminMode = false): Promise<RepairCase> {
  const headers = adminMode ? authHeaders() : {};
  const res = await fetch(`${API_BASE}/api/cms/repair-cases/${encodeURIComponent(idOrSlug)}`, { headers });
  if (res.status === 401) throw new Error('Unauthorized');
  if (!res.ok) throw new Error(`Case not found: ${res.status}`);
  const { case: repairCase } = await res.json() as { case: RepairCase };
  return repairCase;
}

async function throwIfNotOk(res: Response, fallback: string): Promise<never> {
  if (res.status === 401) throw new Error('Unauthorized');
  const err = await res.json().catch(() => ({})) as { error?: string };
  throw new Error(err.error ?? `${fallback} (${res.status})`);
}

export async function createRepairCase(data: RepairCaseDraft): Promise<RepairCase> {
  const res = await fetch(`${API_BASE}/api/cms/repair-cases`, {
    method: 'POST',
    headers: json(authHeaders()),
    body: JSON.stringify(data),
  });
  if (!res.ok) await throwIfNotOk(res, 'Create failed');
  const { case: repairCase } = await res.json() as { case: RepairCase };
  return repairCase;
}

export async function saveRepairCase(id: string, data: Partial<RepairCaseDraft>): Promise<RepairCase> {
  const res = await fetch(`${API_BASE}/api/cms/repair-cases/${id}`, {
    method: 'PUT',
    headers: json(authHeaders()),
    body: JSON.stringify(data),
  });
  if (!res.ok) await throwIfNotOk(res, 'Save failed');
  const { case: repairCase } = await res.json() as { case: RepairCase };
  return repairCase;
}

export async function deleteRepairCase(id: string): Promise<void> {
  const res = await fetch(`${API_BASE}/api/cms/repair-cases/${id}`, {
    method: 'DELETE',
    headers: authHeaders(),
  });
  if (!res.ok) await throwIfNotOk(res, 'Delete failed');
}

export async function searchRepairCases(q: string, filters: {
  device?: string;
  board_revision?: string;
  category?: string;
  result?: string;
} = {}): Promise<RepairCase[]> {
  const params = new URLSearchParams({ q });
  if (filters.device)         params.set('device', filters.device);
  if (filters.board_revision) params.set('board_revision', filters.board_revision);
  if (filters.category)       params.set('category', filters.category);
  if (filters.result)         params.set('result', filters.result);
  const res = await fetch(`${API_BASE}/api/cms/repair-cases/search?${params.toString()}`);
  if (!res.ok) throw new Error('Search failed');
  const body = await res.json() as { cases: RepairCase[]; fallback_used?: boolean };
  if (body.fallback_used) console.warn('[repair-kb] CMS FALLBACK USED — cases search returned empty due to Supabase error');
  return body.cases;
}

export async function fetchSuggestions(field: 'board_revision' | 'device_family' | 'device_model'): Promise<string[]> {
  const res = await fetch(`${API_BASE}/api/cms/repair-cases/suggestions?field=${field}`, {
    headers: authHeaders(),
  });
  if (!res.ok) return [];
  const { suggestions } = await res.json() as { suggestions: string[] };
  return suggestions;
}

// ── Media ─────────────────────────────────────────────────────────────────────

export async function getRepairMediaUploadUrl(caseId: string, mimeType: string, caption = ''): Promise<{
  signedUrl: string; path: string; publicUrl: string;
}> {
  const res = await fetch(`${API_BASE}/api/cms/repair-cases/${caseId}/upload-url`, {
    method: 'POST',
    headers: json(authHeaders()),
    body: JSON.stringify({ mimeType, caption }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({})) as { error?: string };
    throw new Error(err.error ?? 'Could not get upload URL');
  }
  return res.json() as Promise<{ signedUrl: string; path: string; publicUrl: string }>;
}

export async function uploadRepairMedia(
  caseId: string,
  file: File,
  onProgress?: (pct: number) => void,
): Promise<RepairMedia> {
  // 1. Get signed URL
  const { signedUrl, path, publicUrl } = await getRepairMediaUploadUrl(caseId, file.type);

  // 2. Upload directly to Supabase Storage
  await new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', signedUrl);
    xhr.setRequestHeader('Content-Type', file.type);
    xhr.upload.onprogress = e => { if (e.lengthComputable && onProgress) onProgress(Math.round(e.loaded / e.total * 100)); };
    xhr.onload  = () => (xhr.status < 300 ? resolve() : reject(new Error(`Upload failed: ${xhr.status}`)));
    xhr.onerror = () => reject(new Error('Network error during upload'));
    xhr.send(file);
  });

  // 3. Register in DB
  const res = await fetch(`${API_BASE}/api/cms/repair-cases/${caseId}/media`, {
    method: 'POST',
    headers: json(authHeaders()),
    body: JSON.stringify({ storage_path: path, public_url: publicUrl, mime_type: file.type, caption: '' }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({})) as { error?: string };
    throw new Error(err.error ?? 'Failed to register media');
  }
  const { media } = await res.json() as { media: RepairMedia };
  return media;
}

export async function updateRepairMedia(caseId: string, mediaId: string, patch: {
  caption?: string; sort_order?: number; is_featured?: boolean;
}): Promise<RepairMedia> {
  const res = await fetch(`${API_BASE}/api/cms/repair-cases/${caseId}/media/${mediaId}`, {
    method: 'PUT',
    headers: json(authHeaders()),
    body: JSON.stringify(patch),
  });
  if (!res.ok) throw new Error('Failed to update media');
  const { media } = await res.json() as { media: RepairMedia };
  return media;
}

export async function deleteRepairMedia(caseId: string, mediaId: string): Promise<void> {
  const res = await fetch(`${API_BASE}/api/cms/repair-cases/${caseId}/media/${mediaId}`, {
    method: 'DELETE',
    headers: authHeaders(),
  });
  if (!res.ok) throw new Error('Failed to delete media');
}

// ── React hooks ───────────────────────────────────────────────────────────────

export function useRepairCases(adminMode = false) {
  const [cases, setCases]     = useState<RepairCase[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState<string | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    fetchRepairCases(adminMode)
      .then(data => { setCases(data); setLoading(false); })
      .catch(e  => { setError(e.message); setLoading(false); });
  }, [adminMode]);

  useEffect(() => { load(); }, [load]);

  return { cases, loading, error, refetch: load };
}

export function useRepairCase(idOrSlug: string, adminMode = false) {
  const [repairCase, setRepairCase] = useState<RepairCase | null>(null);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState<string | null>(null);

  useEffect(() => {
    if (!idOrSlug) return;
    setLoading(true);
    setError(null);
    fetchRepairCase(idOrSlug, adminMode)
      .then(c  => { setRepairCase(c); setLoading(false); })
      .catch(e => { setError(e.message); setLoading(false); });
  }, [idOrSlug, adminMode]);

  return { repairCase, loading, error };
}

// ── Empty row factories (for form "Add row" buttons) ─────────────────────────

export function emptyError(): RepairError {
  return { source: 'uart', error_code: '', notes: '', sort_order: 0 };
}

export function emptyMeasurement(): RepairMeasurement {
  return {
    reference_line: '', measurement_type: 'voltage',
    expected_value: '', measured_value: '', units: 'V',
    is_verified_reference: false, notes: '', sort_order: 0,
  };
}

export function emptyVoltageInjection(): RepairVoltageInjection {
  return { injection_point: '', injected_voltage: '', current_limit: '', result: '', component_heated: '', notes: '', sort_order: 0 };
}

export function emptyComponent(): RepairComponent {
  return { component_ref: '', component_type: 'capacitor', failure_mode: 'shorted', notes: '', sort_order: 0 };
}

// ── Constants ─────────────────────────────────────────────────────────────────

export const DEVICE_FAMILIES = [
  { value: 'ps5',           label: 'PlayStation 5' },
  { value: 'xbox-series-x', label: 'Xbox Series X' },
  { value: 'xbox-series-s', label: 'Xbox Series S' },
  { value: 'switch',        label: 'Nintendo Switch' },
  { value: 'steam-deck',    label: 'Steam Deck' },
  { value: 'rog-ally',      label: 'ROG Ally' },
  { value: 'legion-go',     label: 'Legion Go' },
  { value: 'ps4',           label: 'PlayStation 4' },
  { value: 'xbox-one',      label: 'Xbox One' },
  { value: 'other',         label: 'Other' },
];

export const REPAIR_CATEGORIES = [
  'No Power', 'No Display', 'Blue Light of Death', 'Overheating',
  'HDMI', 'Disc Drive', 'USB', 'Network', 'Storage',
  'Power Supply', 'Short Circuit', 'Controller', 'Other',
];

export const RESULT_OPTIONS: { value: RepairResult; label: string }[] = [
  { value: '',                    label: 'Not set' },
  { value: 'repaired',            label: 'Repaired' },
  { value: 'partially-repaired',  label: 'Partially Repaired' },
  { value: 'no-fix',              label: 'No Fix' },
  { value: 'board-donor',         label: 'Board Donor' },
  { value: 'further-diagnosis',   label: 'Further Diagnosis Required' },
];

export const MEASUREMENT_TYPES: { value: MeasurementType; label: string }[] = [
  { value: 'voltage',     label: 'Voltage' },
  { value: 'resistance',  label: 'Resistance to Ground' },
  { value: 'diode',       label: 'Diode Mode' },
  { value: 'current',     label: 'Current Draw' },
  { value: 'injection',   label: 'Voltage Injection' },
  { value: 'continuity',  label: 'Continuity' },
  { value: 'other',       label: 'Other' },
];

export const MEASUREMENT_UNITS: { value: MeasurementUnit; label: string }[] = [
  { value: 'V',     label: 'V' },
  { value: 'mV',    label: 'mV' },
  { value: 'Ω',     label: 'Ω' },
  { value: 'kΩ',    label: 'kΩ' },
  { value: 'MΩ',    label: 'MΩ' },
  { value: 'A',     label: 'A' },
  { value: 'mA',    label: 'mA' },
  { value: 'diode', label: 'diode value' },
  { value: 'other', label: 'other' },
];

export const ERROR_SOURCES: { value: ErrorSource; label: string }[] = [
  { value: 'uart',                label: 'UART' },
  { value: 'console-service-tool', label: 'Console Service Tool' },
  { value: 'system-error-code',   label: 'System Error Code' },
  { value: 'other',               label: 'Other' },
];

export const COMPONENT_TYPES: { value: ComponentType; label: string }[] = [
  { value: 'capacitor',     label: 'Capacitor' },
  { value: 'fuse',          label: 'Fuse' },
  { value: 'mosfet',        label: 'MOSFET' },
  { value: 'ic',            label: 'IC' },
  { value: 'hdmi-encoder',  label: 'HDMI Encoder' },
  { value: 'southbridge',   label: 'Southbridge' },
  { value: 'ram',           label: 'RAM' },
  { value: 'ssd-controller', label: 'SSD Controller' },
  { value: 'power-ic',      label: 'Power IC' },
  { value: 'connector',     label: 'Connector' },
  { value: 'other',         label: 'Other' },
];

export const FAILURE_MODES: { value: FailureMode; label: string }[] = [
  { value: 'shorted',           label: 'Shorted' },
  { value: 'open',              label: 'Open' },
  { value: 'corroded',          label: 'Corroded' },
  { value: 'missing',           label: 'Missing' },
  { value: 'cracked',           label: 'Cracked' },
  { value: 'burned',            label: 'Burned' },
  { value: 'incorrect-reading', label: 'Incorrect Reading' },
  { value: 'other',             label: 'Other' },
];

// ══════════════════════════════════════════════════════════════════════════════
// Known-Good Reference types and API
// ══════════════════════════════════════════════════════════════════════════════

export type RefMeasurementType =
  'diode-mode' | 'voltage' | 'other' |
  // legacy values kept for backward compat with existing records
  'resistance-to-ground' | 'resistance' | 'continuity' | 'current-draw' | 'board-reference';
export type RefCategory =
  'hdmi' | 'power-rails' | 'fuses' | 'usb' | 'southbridge' | 'ssd' |
  'ram' | 'disc-drive' | 'network' | 'standby-rails' | 'other';
export type RefSourceType = 'gamefix' | 'manufacturer' | 'community' | 'other';
export type RefVerification = 'observed' | 'confirmed' | 'verified';

export interface RefAnnotation {
  id?:              string;
  media_id:         string;
  reference_id?:    string;
  label:            string;
  x_percent:        number;
  y_percent:        number;
  measurement_type: RefMeasurementType | '';
  measured_value:   string;
  units:            string;
  notes:            string;
  sort_order:       number;
}

export interface RefMedia {
  id?:           string;
  reference_id?: string;
  storage_path:  string;
  public_url:    string;
  mime_type:     string;
  caption:       string;
  sort_order:    number;
  created_at?:   string;
  repair_reference_annotations?: RefAnnotation[];
}

export interface RefMeasurement {
  id?:              string;
  reference_id?:    string;
  label:            string;
  measurement_type: RefMeasurementType | '';
  measured_value:   string;
  units:            string;
  notes:            string;
  sort_order:       number;
}

export interface RepairReference {
  id:                   string;
  slug:                 string;
  status:               RepairStatus;
  title:                string;
  seo_title:            string;
  seo_description:      string;
  device_family:        string;
  device_model:         string;
  board_revision:       string;
  device_family_id:     string | null;
  model_id:             string | null;
  board_revision_id:    string | null;
  category:             RefCategory | '';
  measurement_type:     RefMeasurementType | '';
  testing_conditions:   string;
  notes:                string;
  source_type:          RefSourceType;
  source_url:           string;
  verification_status:  RefVerification;
  confirmed_board_count: number;
  internal_notes?:      string;
  tags:                 string[];
  published_at:         string | null;
  created_at:           string;
  updated_at:           string;
  repair_reference_media?:        RefMedia[];
  repair_reference_measurements?: RefMeasurement[];
}

export type RepairReferenceDraft = Omit<RepairReference,
  'id' | 'slug' | 'created_at' | 'updated_at' | 'published_at'
> & {
  slug?:         string;
  measurements?: RefMeasurement[];
};

// ── Reference API ─────────────────────────────────────────────────────────────

async function throwRefError(res: Response, fallback: string): Promise<never> {
  if (res.status === 401) throw new Error('Unauthorized');
  const err = await res.json().catch(() => ({})) as { error?: string };
  throw new Error(err.error ?? `${fallback} (${res.status})`);
}

export async function fetchRepairReferences(adminMode = false): Promise<RepairReference[]> {
  const headers = adminMode ? authHeaders() : {};
  const res = await fetch(`${API_BASE}/api/cms/repair-references`, { headers });
  if (res.status === 401) throw new Error('Unauthorized');
  if (!res.ok) throw new Error(`Failed to fetch references: ${res.status}`);
  const body = await res.json() as { references: RepairReference[]; fallback_used?: boolean };
  if (body.fallback_used) console.warn('[repair-kb] CMS FALLBACK USED — references list returned empty due to Supabase error');
  return body.references;
}

export async function fetchRepairReference(idOrSlug: string, adminMode = false): Promise<RepairReference> {
  const headers = adminMode ? authHeaders() : {};
  const res = await fetch(
    `${API_BASE}/api/cms/repair-references/${encodeURIComponent(idOrSlug)}`,
    { headers },
  );
  if (res.status === 401) throw new Error('Unauthorized');
  if (!res.ok) throw new Error(`Reference not found: ${res.status}`);
  const { reference } = await res.json() as { reference: RepairReference };
  return reference;
}

export async function createRepairReference(data: RepairReferenceDraft): Promise<RepairReference> {
  const res = await fetch(`${API_BASE}/api/cms/repair-references`, {
    method: 'POST',
    headers: json(authHeaders()),
    body: JSON.stringify(data),
  });
  if (!res.ok) await throwRefError(res, 'Create failed');
  const { reference } = await res.json() as { reference: RepairReference };
  return reference;
}

export async function saveRepairReference(id: string, data: Partial<RepairReferenceDraft>): Promise<RepairReference> {
  const res = await fetch(`${API_BASE}/api/cms/repair-references/${id}`, {
    method: 'PUT',
    headers: json(authHeaders()),
    body: JSON.stringify(data),
  });
  if (!res.ok) await throwRefError(res, 'Save failed');
  const { reference } = await res.json() as { reference: RepairReference };
  return reference;
}

export async function deleteRepairReference(id: string): Promise<void> {
  const res = await fetch(`${API_BASE}/api/cms/repair-references/${id}`, {
    method: 'DELETE',
    headers: authHeaders(),
  });
  if (!res.ok) await throwRefError(res, 'Delete failed');
}

export async function searchRepairReferences(
  q: string,
  opts: { device?: string; category?: string; measurement_type?: string } = {},
): Promise<RepairReference[]> {
  const params = new URLSearchParams();
  if (q)                      params.set('q', q);
  if (opts.device)            params.set('device', opts.device);
  if (opts.category)          params.set('category', opts.category);
  if (opts.measurement_type)  params.set('measurement_type', opts.measurement_type);
  const res = await fetch(`${API_BASE}/api/cms/repair-references/search?${params}`);
  if (!res.ok) throw new Error(`Search failed: ${res.status}`);
  const body = await res.json() as { references: RepairReference[]; fallback_used?: boolean };
  if (body.fallback_used) console.warn('[repair-kb] CMS FALLBACK USED — references search returned empty due to Supabase error');
  return body.references;
}

// ── Reference media ───────────────────────────────────────────────────────────

export async function getRefMediaUploadUrl(refId: string, mimeType: string): Promise<{
  signedUrl: string; path: string; publicUrl: string;
}> {
  const res = await fetch(`${API_BASE}/api/cms/repair-references/${refId}/upload-url`, {
    method: 'POST',
    headers: json(authHeaders()),
    body: JSON.stringify({ mimeType }),
  });
  if (!res.ok) await throwRefError(res, 'Could not get upload URL');
  return res.json() as Promise<{ signedUrl: string; path: string; publicUrl: string }>;
}

export async function uploadRefMedia(
  refId: string,
  file: File,
  onProgress?: (pct: number) => void,
): Promise<RefMedia> {
  const { signedUrl, path, publicUrl } = await getRefMediaUploadUrl(refId, file.type);

  await new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', signedUrl);
    xhr.setRequestHeader('Content-Type', file.type);
    xhr.upload.onprogress = e => {
      if (e.lengthComputable && onProgress) onProgress(Math.round(e.loaded / e.total * 100));
    };
    xhr.onload  = () => (xhr.status < 300 ? resolve() : reject(new Error(`Upload failed: ${xhr.status}`)));
    xhr.onerror = () => reject(new Error('Network error during upload'));
    xhr.send(file);
  });

  const res = await fetch(`${API_BASE}/api/cms/repair-references/${refId}/media`, {
    method: 'POST',
    headers: json(authHeaders()),
    body: JSON.stringify({ storage_path: path, public_url: publicUrl, mime_type: file.type, caption: '' }),
  });
  if (!res.ok) await throwRefError(res, 'Failed to register media');
  const { media } = await res.json() as { media: RefMedia };
  return media;
}

export async function deleteRefMedia(refId: string, mediaId: string): Promise<void> {
  const res = await fetch(`${API_BASE}/api/cms/repair-references/${refId}/media/${mediaId}`, {
    method: 'DELETE',
    headers: authHeaders(),
  });
  if (!res.ok) throw new Error('Failed to delete media');
}

export async function updateRefMedia(
  refId: string,
  mediaId: string,
  data: { sort_order?: number; caption?: string },
): Promise<void> {
  const res = await fetch(`${API_BASE}/api/cms/repair-references/${refId}/media/${mediaId}`, {
    method: 'PUT',
    headers: json(authHeaders()),
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error('Failed to update media');
}

// ── Annotations ───────────────────────────────────────────────────────────────

export async function addAnnotation(
  refId: string, mediaId: string, data: Omit<RefAnnotation, 'id' | 'media_id' | 'reference_id'>,
): Promise<RefAnnotation> {
  const res = await fetch(
    `${API_BASE}/api/cms/repair-references/${refId}/media/${mediaId}/annotations`,
    { method: 'POST', headers: json(authHeaders()), body: JSON.stringify(data) },
  );
  if (!res.ok) await throwRefError(res, 'Failed to add annotation');
  const { annotation } = await res.json() as { annotation: RefAnnotation };
  return annotation;
}

export async function updateAnnotation(
  refId: string, mediaId: string, annotId: string,
  data: Partial<Omit<RefAnnotation, 'id' | 'media_id' | 'reference_id'>>,
): Promise<RefAnnotation> {
  const res = await fetch(
    `${API_BASE}/api/cms/repair-references/${refId}/media/${mediaId}/annotations/${annotId}`,
    { method: 'PUT', headers: json(authHeaders()), body: JSON.stringify(data) },
  );
  if (!res.ok) await throwRefError(res, 'Failed to update annotation');
  const { annotation } = await res.json() as { annotation: RefAnnotation };
  return annotation;
}

export async function deleteAnnotation(refId: string, mediaId: string, annotId: string): Promise<void> {
  const res = await fetch(
    `${API_BASE}/api/cms/repair-references/${refId}/media/${mediaId}/annotations/${annotId}`,
    { method: 'DELETE', headers: authHeaders() },
  );
  if (!res.ok) throw new Error('Failed to delete annotation');
}

// ── Reference hooks ───────────────────────────────────────────────────────────

export function useRepairReferences(adminMode = false) {
  const [references, setReferences] = useState<RepairReference[]>([]);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState<string | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    fetchRepairReferences(adminMode)
      .then(data => { setReferences(data); setLoading(false); })
      .catch(e  => { setError(e.message); setLoading(false); });
  }, [adminMode]);

  useEffect(() => { load(); }, [load]);

  return { references, loading, error, refetch: load };
}

// ── Reference constants ───────────────────────────────────────────────────────

export const REF_CATEGORIES: { value: RefCategory; label: string }[] = [
  { value: 'hdmi',          label: 'HDMI' },
  { value: 'power-rails',   label: 'Power Rails' },
  { value: 'fuses',         label: 'Fuses' },
  { value: 'usb',           label: 'USB' },
  { value: 'southbridge',   label: 'Southbridge' },
  { value: 'ssd',           label: 'SSD' },
  { value: 'ram',           label: 'RAM' },
  { value: 'disc-drive',    label: 'Disc Drive' },
  { value: 'network',       label: 'Network' },
  { value: 'standby-rails', label: 'Standby Rails' },
  { value: 'other',         label: 'Other' },
];

export const REF_MEASUREMENT_TYPES: { value: RefMeasurementType; label: string; shortLabel: string }[] = [
  { value: 'diode-mode', label: 'Diode Mode', shortLabel: 'Diode Mode' },
  { value: 'voltage',    label: 'Voltage',    shortLabel: 'Voltage' },
  { value: 'other',      label: 'Other',      shortLabel: 'Other' },
];

// All known labels including legacy values (for display on existing records)
export function measurementTypeLabel(v: string): string {
  const map: Record<string, string> = {
    'diode-mode':           'Diode Mode',
    'voltage':              'Voltage',
    'other':                'Other',
    'resistance-to-ground': 'Resistance to Ground',
    'resistance':           'Resistance',
    'continuity':           'Continuity',
    'current-draw':         'Current Draw',
    'board-reference':      'Board Reference',
  };
  return map[v] ?? v;
}

export const REF_SOURCE_TYPES: { value: RefSourceType; label: string }[] = [
  { value: 'gamefix',      label: 'GameFix Measurement' },
  { value: 'manufacturer', label: 'Manufacturer Documentation' },
  { value: 'community',    label: 'Community Reference' },
  { value: 'other',        label: 'Other' },
];

export const REF_VERIFICATION: { value: RefVerification; label: string; description: string }[] = [
  { value: 'observed', label: 'Observed',           description: 'Measured on one board' },
  { value: 'confirmed', label: 'Repeated / Confirmed', description: 'Measured on multiple boards' },
  { value: 'verified', label: 'Verified Reference', description: 'Cross-referenced with documentation' },
];

// ── Cross-link types ──────────────────────────────────────────────────────────

export interface LinkedReference {
  id: string;
  slug: string | null;
  title: string;
  category: string | null;
  device_family: string | null;
  device_model: string | null;
  board_revision: string | null;
  verification_status: string | null;
  repair_reference_media?: { id: string; public_url: string; sort_order: number }[];
}

export interface LinkedCase {
  id: string;
  slug: string;
  title: string;
  device_family: string;
  device_model: string;
  board_revision: string;
  result: string;
  published_at: string | null;
  featured_image_url: string | null;
}

export async function fetchLinkedReferences(caseId: string): Promise<LinkedReference[]> {
  const res = await fetch(`${API_BASE}/api/cms/repair-cases/${caseId}/references`);
  if (!res.ok) throw new Error(`Failed to fetch linked references: ${res.status}`);
  const { references } = await res.json() as { references: LinkedReference[] };
  return references;
}

export async function fetchLinkedCases(refId: string): Promise<LinkedCase[]> {
  const res = await fetch(`${API_BASE}/api/cms/repair-references/${refId}/cases`);
  if (!res.ok) throw new Error(`Failed to fetch linked cases: ${res.status}`);
  const { cases } = await res.json() as { cases: LinkedCase[] };
  return cases;
}

export async function linkCaseToReference(caseId: string, referenceId: string): Promise<void> {
  const res = await fetch(`${API_BASE}/api/cms/repair-cases/${caseId}/references`, {
    method: 'POST',
    headers: json(authHeaders()),
    body: JSON.stringify({ reference_id: referenceId }),
  });
  if (!res.ok) await throwIfNotOk(res, 'Link failed');
}

export async function unlinkCaseFromReference(caseId: string, referenceId: string): Promise<void> {
  const res = await fetch(`${API_BASE}/api/cms/repair-cases/${caseId}/references/${referenceId}`, {
    method: 'DELETE',
    headers: authHeaders(),
  });
  if (!res.ok) await throwIfNotOk(res, 'Unlink failed');
}

// ── Taxonomy types ────────────────────────────────────────────────────────────

export interface KbManufacturer {
  id:         string;
  name:       string;
  slug:       string;
  sort_order: number;
}

export interface KbDeviceFamily {
  id:              string;
  manufacturer_id: string;
  name:            string;
  slug:            string;
  sort_order:      number;
}

export interface KbModel {
  id:               string;
  device_family_id: string;
  name:             string;
  slug:             string;
  sort_order:       number;
}

export interface KbBoardRevision {
  id:               string;
  device_family_id: string;
  name:             string;
  slug:             string;
  aliases:          string[];
  sort_order:       number;
}

export interface KbTaxonomy {
  manufacturers:   KbManufacturer[];
  device_families: KbDeviceFamily[];
  models:          KbModel[];
  board_revisions: KbBoardRevision[];
}

// ── Taxonomy helpers ──────────────────────────────────────────────────────────

export function familiesForManufacturer(taxonomy: KbTaxonomy, mfrId: string): KbDeviceFamily[] {
  return taxonomy.device_families
    .filter(f => f.manufacturer_id === mfrId)
    .sort((a, b) => a.sort_order - b.sort_order);
}

export function modelsForFamily(taxonomy: KbTaxonomy, familyId: string): KbModel[] {
  return taxonomy.models
    .filter(m => m.device_family_id === familyId)
    .sort((a, b) => a.sort_order - b.sort_order);
}

export function revisionsForFamily(taxonomy: KbTaxonomy, familyId: string): KbBoardRevision[] {
  return taxonomy.board_revisions
    .filter(b => b.device_family_id === familyId)
    .sort((a, b) => a.sort_order - b.sort_order);
}

export function findFamily(taxonomy: KbTaxonomy, slug: string): KbDeviceFamily | undefined {
  return taxonomy.device_families.find(f => f.slug === slug);
}

export function findRevision(taxonomy: KbTaxonomy, slug: string): KbBoardRevision | undefined {
  return taxonomy.board_revisions.find(b => b.slug === slug);
}

export function findManufacturer(taxonomy: KbTaxonomy, id: string): KbManufacturer | undefined {
  return taxonomy.manufacturers.find(m => m.id === id);
}

// ── Taxonomy API ──────────────────────────────────────────────────────────────

let _taxonomyCache: { data: KbTaxonomy; fetchedAt: number } | null = null;
const TAXONOMY_TTL = 5 * 60 * 1000;

export async function fetchTaxonomy(): Promise<KbTaxonomy> {
  if (_taxonomyCache && Date.now() - _taxonomyCache.fetchedAt < TAXONOMY_TTL) {
    return _taxonomyCache.data;
  }
  const res = await fetch(`${API_BASE}/api/cms/taxonomy/full`);
  if (!res.ok) throw new Error(`Failed to fetch taxonomy: ${res.status}`);
  const body = await res.json() as KbTaxonomy & { fallback_used?: boolean };
  if (body.fallback_used) console.warn('[repair-kb] taxonomy tables not accessible — DB migration may be pending');
  const data: KbTaxonomy = {
    manufacturers:   Array.isArray(body.manufacturers)   ? body.manufacturers   : [],
    device_families: Array.isArray(body.device_families) ? body.device_families : [],
    models:          Array.isArray(body.models)          ? body.models          : [],
    board_revisions: Array.isArray(body.board_revisions) ? body.board_revisions : [],
  };
  _taxonomyCache = { data, fetchedAt: Date.now() };
  return data;
}

export function invalidateTaxonomyCache(): void {
  _taxonomyCache = null;
}

// ── Admin taxonomy CRUD ───────────────────────────────────────────────────────

type TaxonomyEntity = 'manufacturers' | 'device-families' | 'models' | 'board-revisions';

export async function createTaxonomyItem(
  entity: TaxonomyEntity,
  body: Record<string, unknown>,
): Promise<Record<string, unknown>> {
  const res = await fetch(`${API_BASE}/api/cms/taxonomy/${entity}`, {
    method: 'POST',
    headers: json(authHeaders()),
    body: JSON.stringify(body),
  });
  if (!res.ok) await throwIfNotOk(res, 'Create failed');
  invalidateTaxonomyCache();
  const { item } = await res.json() as { item: Record<string, unknown> };
  return item;
}

export async function updateTaxonomyItem(
  entity: TaxonomyEntity,
  id: string,
  body: Record<string, unknown>,
): Promise<Record<string, unknown>> {
  const res = await fetch(`${API_BASE}/api/cms/taxonomy/${entity}/${id}`, {
    method: 'PUT',
    headers: json(authHeaders()),
    body: JSON.stringify(body),
  });
  if (!res.ok) await throwIfNotOk(res, 'Update failed');
  invalidateTaxonomyCache();
  const { item } = await res.json() as { item: Record<string, unknown> };
  return item;
}

export async function deleteTaxonomyItem(entity: TaxonomyEntity, id: string): Promise<void> {
  const res = await fetch(`${API_BASE}/api/cms/taxonomy/${entity}/${id}`, {
    method: 'DELETE',
    headers: authHeaders(),
  });
  if (!res.ok) await throwIfNotOk(res, 'Delete failed');
  invalidateTaxonomyCache();
}

// ── useTaxonomy hook ──────────────────────────────────────────────────────────

export function useTaxonomy() {
  const [taxonomy, setTaxonomy] = useState<KbTaxonomy>({ manufacturers: [], device_families: [], models: [], board_revisions: [] });
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState<string | null>(null);

  useEffect(() => {
    fetchTaxonomy()
      .then(t  => { setTaxonomy(t); setLoading(false); })
      .catch(e => { setError(e.message); setLoading(false); });
  }, []);

  return { taxonomy, loading, error };
}
